<template>
  <label
    class="smardie-checkbox"
    :class="{
      'smardie-checkbox--checked': isChecked,
      'smardie-checkbox--disabled': disabled,
      'smardie-checkbox--attachment': isAttachment
    }"
  >
    <template v-if="isAttachment">
      <span class="smardie-checkbox__attachment-icon" aria-hidden="true">
        <svg v-if="!icon" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="#4DA1FF" stroke-width="1.5" fill="#E7F5FE"/>
          <path d="M14 3v5h5" stroke="#4DA1FF" stroke-width="1.5" fill="none"/>
          <path d="M9 12h6M9 15h6M9 18h4" stroke="#4DA1FF" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <img v-else :src="icon" alt="" width="28" height="28">
      </span>
      <span class="smardie-checkbox__attachment-text">
        <span class="smardie-checkbox__attachment-title">{{ title || label }}</span>
        <span v-if="subtitle" class="smardie-checkbox__attachment-subtitle">{{ subtitle }}</span>
      </span>
      <input
        type="checkbox"
        class="smardie-checkbox__input"
        :checked="isChecked"
        :disabled="disabled"
        @change="$emit('update:modelValue', $event.target.checked)"
      >
      <span class="smardie-checkbox__box">
        <svg v-show="isChecked" class="smardie-checkbox__tick" viewBox="0 0 24 24" width="14" height="14">
          <path fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M4 12l5 5 12-12"/>
        </svg>
      </span>
    </template>
    <template v-else>
      <input
        type="checkbox"
        class="smardie-checkbox__input"
        :checked="isChecked"
        :disabled="disabled"
        @change="$emit('update:modelValue', $event.target.checked)"
      >
      <span class="smardie-checkbox__box">
        <svg v-show="isChecked" class="smardie-checkbox__tick" viewBox="0 0 24 24" width="14" height="14">
          <path fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" d="M4 12l5 5 12-12"/>
        </svg>
      </span>
      <span class="smardie-checkbox__label"><slot>{{ label }}</slot></span>
    </template>
  </label>
</template>

<script>
export default {
  name: 'Checkbox',
  compatConfig: {
    COMPONENT_V_MODEL: false
  },
  props: {
    modelValue: { type: Boolean, default: false },
    label: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    variant: { type: String, default: 'default' },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    icon: { type: String, default: '' }
  },
  computed: {
    isChecked () {
      return !!this.modelValue
    },
    isAttachment () {
      return this.variant === 'attachment'
    }
  }
}
</script>

<style scoped>
/* Material Design–style checkbox: 1px stroke (Grey Light), Blue Primary fill when checked, white tick */
.smardie-checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin: 0;
  font-weight: normal;
  color: var(--color-text);
}

.smardie-checkbox__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  pointer-events: none;
}

.smardie-checkbox__box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  box-sizing: border-box;
  border: 1px solid var(--color-border);  /* Grey Light stroke */
  border-radius: 2px;
  background: var(--color-bg-surface);
  margin-right: 10px;
  vertical-align: middle;
  transition: background 0.15s ease, border-color 0.15s ease;
}

[data-theme="dark"] .smardie-checkbox__box {
  border-color: var(--color-primary); /* #4DA1FF */
}

.smardie-checkbox__tick {
  display: block;
  pointer-events: none;
}

.smardie-checkbox__label {
  line-height: 1.2;
}

/* Checked: Blue Primary fill, same 1px Grey Light stroke, white tick */
.smardie-checkbox--checked .smardie-checkbox__box {
  background: #4DA1FF;  /* Primary Blue */
  border-color: var(--color-border);
}

.smardie-checkbox:focus-within .smardie-checkbox__box {
  outline: none;
}

.smardie-checkbox--checked:focus-within .smardie-checkbox__box {
  background: #4DA1FF;
  border-color: var(--color-border);
}

[data-theme="dark"] .smardie-checkbox--checked .smardie-checkbox__box,
[data-theme="dark"] .smardie-checkbox--checked:focus-within .smardie-checkbox__box {
  border-color: var(--color-primary);
}

.smardie-checkbox--disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

/* Attachment variant — send-quote modal card
   Must beat `.ui.form label { display: block }` so icon | text | checkbox stay on one row. */
.smardie-checkbox--attachment {
  position: relative;
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  margin: 0 !important;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-surface);
  gap: 12px;
  font-weight: normal;
}

.smardie-checkbox--attachment .smardie-checkbox__box {
  margin-right: 0;
  margin-left: auto;
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  border-radius: 4px;
  flex-shrink: 0;
  align-self: center;
}

.smardie-checkbox--attachment.smardie-checkbox--checked {
  border-color: #CED0DA;
}

.smardie-checkbox__attachment-icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  align-self: center;
}

.smardie-checkbox__attachment-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
}

.smardie-checkbox__attachment-title {
  font-family: var(--font-semibold, inherit);
  font-weight: 600;
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.3;
  word-break: break-word;
}

.smardie-checkbox__attachment-subtitle {
  font-weight: 400;
  font-size: 14px;
  color: var(--color-text);
  line-height: 1.3;
}
</style>
